/**
 * Midnight Theme Configuration
 *
 * A near-black theme with high contrast accents and subtle glow effects.
 * Inspired by modern dashboard designs with deep, immersive backgrounds.
 *
 * Key differences from dark theme:
 * - Near-black backgrounds (#0a0a0a vs #0A1929)
 * - Higher contrast borders
 * - Subtle glow effects on interactive elements
 * - More pronounced accent colors
 */

import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { baseColors, midnightPalette } from './palette';
import { darkShadows, focusRings, glowEffects } from './shadows';
import { muiTransitions, transitionPresets } from './transitions';
import { typographyVariants } from './typography';

// ============================================================================
// Midnight-Specific Effects
// ============================================================================

const midnightGlass = {
  background: 'rgba(20, 20, 20, 0.95)',
  backgroundHover: 'rgba(30, 30, 30, 0.95)',
  backdropBlur: 'blur(20px)',
  border: 'rgba(255, 255, 255, 0.08)',
  borderHover: 'rgba(255, 255, 255, 0.12)',
  shadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  shadowHover: '0 12px 48px rgba(0, 0, 0, 0.5)',
};

// ============================================================================
// Midnight Theme Options
// ============================================================================

const midnightThemeOptions: ThemeOptions = {
  palette: midnightPalette,
  typography: typographyVariants,
  transitions: muiTransitions,
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    darkShadows[1],
    darkShadows[2],
    darkShadows[2],
    darkShadows[3],
    darkShadows[3],
    darkShadows[3],
    darkShadows[3],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[4],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[5],
    darkShadows[6],
  ],
  components: {
    // ========================================================================
    // CssBaseline
    // ========================================================================
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: midnightPalette.background.default,
          scrollbarColor: 'rgba(255,255,255,0.15) transparent',
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.15)',
            borderRadius: 4,
            '&:hover': {
              background: 'rgba(255,255,255,0.25)',
            },
          },
        },
      },
    },

    // ========================================================================
    // Button
    // ========================================================================
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          fontWeight: 600,
          textTransform: 'none',
          transition: transitionPresets.button,
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.MuiButton-contained.MuiButton-colorPrimary': {
            background: `linear-gradient(135deg, ${baseColors.primary[400]} 0%, ${baseColors.primary[500]} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${baseColors.primary[300]} 0%, ${baseColors.primary[400]} 100%)`,
              boxShadow: `${darkShadows[3]}, ${glowEffects.primary.strong}`,
            },
          },
          '&.MuiButton-contained.MuiButton-colorSecondary': {
            background: `linear-gradient(135deg, ${baseColors.secondary[400]} 0%, ${baseColors.secondary[500]} 100%)`,
            '&:hover': {
              boxShadow: `${darkShadows[3]}, ${glowEffects.secondary.strong}`,
            },
          },
        },
        contained: {
          boxShadow: darkShadows[2],
          '&:hover': {
            boxShadow: `${darkShadows[3]}, ${glowEffects.primary.medium}`,
          },
        },
        outlined: {
          borderWidth: 1.5,
          borderColor: 'rgba(255, 255, 255, 0.15)',
          color: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            borderWidth: 1.5,
            borderColor: 'rgba(255, 255, 255, 0.3)',
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
          },
        },
        text: {
          color: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },

    // ========================================================================
    // Card (Midnight Glass)
    // ========================================================================
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: midnightGlass.background,
          backdropFilter: midnightGlass.backdropBlur,
          border: `1px solid ${midnightGlass.border}`,
          boxShadow: midnightGlass.shadow,
          transition: transitionPresets.card,
          overflow: 'visible',
          '&:hover': {
            background: midnightGlass.backgroundHover,
            border: `1px solid ${midnightGlass.borderHover}`,
            boxShadow: `${midnightGlass.shadowHover}, ${glowEffects.mixed.subtle}`,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '& .MuiCardActionArea-focusHighlight': {
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            opacity: 0,
            transition: transitionPresets.allFast,
          },
          '&:hover .MuiCardActionArea-focusHighlight': {
            opacity: 0.04,
          },
          '&.Mui-focusVisible .MuiCardActionArea-focusHighlight': {
            opacity: 0.08,
          },
        },
      },
    },

    // ========================================================================
    // Paper (Midnight Surface)
    // ========================================================================
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          backgroundColor: midnightPalette.background.paper,
        },
        elevation1: {
          boxShadow: darkShadows[1],
        },
        elevation2: {
          boxShadow: darkShadows[2],
        },
        elevation3: {
          boxShadow: darkShadows[3],
        },
        elevation4: {
          boxShadow: darkShadows[4],
        },
      },
    },

    // ========================================================================
    // TextField / Input
    // ========================================================================
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: 'transparent',
          transition: transitionPresets.input,
          '&:not(.MuiInputBase-multiline):not(.MuiInputBase-sizeSmall)': {
            height: 46,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.18)',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1.5,
            borderColor: baseColors.primary[400],
            boxShadow: focusRings.primary,
          },
        },
        notchedOutline: {
          borderColor: 'rgba(255, 255, 255, 0.08)',
          transition: transitionPresets.input,
        },
        input: {
          backgroundColor: 'transparent',
          '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.35)',
            opacity: 1,
          },
          // Override browser autofill styling
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px transparent inset !important',
            WebkitTextFillColor: 'rgba(255, 255, 255, 0.9) !important',
            caretColor: 'rgba(255, 255, 255, 0.9)',
            transition: 'background-color 5000s ease-in-out 0s',
          },
          '&:-webkit-autofill:hover, &:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 100px transparent inset !important',
            WebkitTextFillColor: 'rgba(255, 255, 255, 0.9) !important',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.5)',
          '&.Mui-focused': {
            color: baseColors.primary[400],
          },
        },
      },
    },

    // ========================================================================
    // Chip
    // ========================================================================
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            boxShadow: glowEffects.primary.subtle,
          },
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.15)',
        },
      },
    },

    // ========================================================================
    // Dialog / Modal (Midnight Glass)
    // ========================================================================
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          background: 'rgba(20, 20, 20, 0.98)',
          backdropFilter: 'blur(24px)',
          boxShadow: `${darkShadows[5]}, ${glowEffects.mixed.medium}`,
          border: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
          padding: '24px 24px 16px',
          color: 'rgba(255, 255, 255, 0.95)',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
          gap: 12,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&:not(.MuiBackdrop-invisible)': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(8px)',
          },
        },
      },
    },

    // ========================================================================
    // Menu / Dropdown
    // ========================================================================
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          background: 'rgba(20, 20, 20, 0.98)',
          backdropFilter: 'blur(20px)',
          boxShadow: darkShadows[4],
          border: '1px solid rgba(255, 255, 255, 0.08)',
          marginTop: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          padding: '12px 16px',
          minHeight: 44,
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(99, 91, 255, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(99, 91, 255, 0.2)',
            },
          },
        },
      },
    },

    // ========================================================================
    // Tooltip
    // ========================================================================
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          color: baseColors.neutral[900],
          borderRadius: 8,
          padding: '8px 12px',
          fontSize: '0.75rem',
          fontWeight: 500,
          boxShadow: darkShadows[3],
        },
        arrow: {
          color: 'rgba(255, 255, 255, 0.95)',
        },
      },
    },

    // ========================================================================
    // Avatar
    // ========================================================================
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: '2px solid rgba(255, 255, 255, 0.08)',
          boxShadow: darkShadows[2],
        },
      },
    },

    // ========================================================================
    // Drawer (Sidebar)
    // ========================================================================
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: midnightPalette.background.default,
          borderRight: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: 0,
          boxShadow: darkShadows[4],
        },
      },
    },

    // ========================================================================
    // AppBar (Midnight Glass)
    // ========================================================================
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: darkShadows[1],
          backdropFilter: 'blur(20px)',
          backgroundColor: 'rgba(10, 10, 10, 0.85)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },

    // ========================================================================
    // FormControlLabel — add breathing room between control and label (switch, checkbox, radio)
    // ========================================================================
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          gap: 12,
        },
      },
    },

    // ========================================================================
    // Switch
    // ========================================================================
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        switchBase: {
          '&.Mui-checked': {
            '& + .MuiSwitch-track': {
              backgroundColor: baseColors.primary[400],
              opacity: 1,
            },
          },
        },
        track: {
          borderRadius: 22,
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
        },
        thumb: {
          boxShadow: darkShadows[2],
        },
      },
    },

    // ========================================================================
    // Skeleton (Shimmer)
    // ========================================================================
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
        },
      },
    },

    // ========================================================================
    // Tabs
    // ========================================================================
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
          backgroundColor: baseColors.primary[400],
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 48,
          color: 'rgba(255, 255, 255, 0.5)',
          transition: transitionPresets.allFast,
          '&:hover': {
            color: 'rgba(255, 255, 255, 0.8)',
          },
          '&.Mui-selected': {
            color: baseColors.primary[400],
          },
        },
      },
    },

    // ========================================================================
    // List
    // ========================================================================
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '4px 8px',
          transition: transitionPresets.allFast,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(99, 91, 255, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(99, 91, 255, 0.2)',
            },
          },
        },
      },
    },

    // ========================================================================
    // Divider
    // ========================================================================
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.06)',
        },
      },
    },

    // ========================================================================
    // Table
    // ========================================================================
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: 'rgba(255, 255, 255, 0.06)',
        },
        head: {
          fontWeight: 600,
          color: 'rgba(255, 255, 255, 0.6)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
          },
        },
      },
    },

    // ========================================================================
    // Alert
    // ========================================================================
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid',
          '&.MuiAlert-standard.MuiAlert-colorSuccess': {
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderColor: 'rgba(16, 185, 129, 0.25)',
          },
          '&.MuiAlert-standard.MuiAlert-colorError': {
            backgroundColor: 'rgba(244, 63, 94, 0.1)',
            borderColor: 'rgba(244, 63, 94, 0.25)',
          },
          '&.MuiAlert-standard.MuiAlert-colorWarning': {
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            borderColor: 'rgba(245, 158, 11, 0.25)',
          },
          '&.MuiAlert-standard.MuiAlert-colorInfo': {
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderColor: 'rgba(59, 130, 246, 0.25)',
          },
        },
      },
    },
  },
};

// ============================================================================
// Create and Export Theme
// ============================================================================

export const midnightTheme = createTheme(midnightThemeOptions);

export default midnightTheme;
